import { Button, Group, Loader, Menu, Table, Text } from '@mantine/core';
import { IconDots, IconDownload, IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import dataCaptureClasses from 'src/data-capture/DataCapture.module.css';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useFactFind } from 'src/fact-find/FactFindProvider';
import { useDeleteFactFind } from 'src/suitability-report/hooks/useDeleteFactFind';
import { useFactFinds } from 'src/suitability-report/hooks/useFactFinds';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import PageLoader from '../../UI/loader/PageLoader';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import FileUploadLayout from './FileUploadLayout';
import EmptyView from './components/EmptyView';
import FileNameWithIcon from './files-table/FileNameWithIcon';
import { dateMatchesSearch, formatDateTime } from 'src/meetings/utils/date-utils';

export default function FactFinds() {
  const { factFinds, isLoading, isError, error, refetch } = useFactFinds();
  const { clientId } = useCurrentClient();
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();
  const { deleteFactFind } = useDeleteFactFind();
  const { open: openFactFindFlow } = useFactFind();
  const [searchValue, setSearchValue] = useState('');

  const handleDownload = async (factFindFileId: number) => {
    try {
      const link = await createLink(factFindFileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download fact find',
        'An error occurred while trying to download the fact find. Please try again later.',
        e,
        { clientId, factFindFileId },
      );
      return;
    }
  };

  const handleDelete = async (factFindId: string) => {
    try {
      await deleteFactFind(clientId, factFindId);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete fact find',
        'An error occurred while trying to delete the fact find. Please try again later.',
        e,
        { clientId, factFindId },
      );
    }

    await refetch();
  };

  const filteredFactFinds = useMemo(() => {
    if (!searchValue.trim()) return factFinds;

    const searchLower = searchValue.toLowerCase();
    return factFinds.filter(
      factFind =>
        (factFind.fileName || '').toLowerCase().includes(searchLower) ||
        dateMatchesSearch(factFind.createdAt, searchLower),
    );
  }, [factFinds, searchValue]);

  const rows = useMemo(
    () =>
      filteredFactFinds.map(factFind => (
        <Table.Tr key={factFind.id}>
          <Table.Td>
            <FileNameWithIcon
              fileName={factFind.fileName || 'No file'}
              isFolder={false}
              isGenerated={factFind.isGenerated}
            />
          </Table.Td>
          <Table.Td>
            <Text size="sm">{formatDateTime(factFind.createdAt)}</Text>
          </Table.Td>
          <Table.Td align="right">
            <Group justify="space-between" wrap="nowrap">
              {factFind.fileId ? (
                <Button
                  variant="primary"
                  onClick={() => handleDownload(factFind.fileId!)}
                  rightSection={<IconDownload size={16} />}
                >
                  Download
                </Button>
              ) : (
                <div />
              )}
              <Menu
                transitionProps={{ transition: 'pop-top-right' }}
                position="bottom-end"
                width={220}
                withinPortal
              >
                <Menu.Target>
                  <IconDots color="#475467" stroke={2} size={20} />
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Item onClick={() => handleDelete(factFind.id)}>Delete</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Table.Td>
        </Table.Tr>
      )),
    [filteredFactFinds],
  );

  if (isError || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (filteredFactFinds.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title="Fact Finds">
        <EmptyView
          title="Capture essential client information"
          subtitle="Generate comprehensive fact find documents with Siesta AI to gather all necessary client details for informed financial planning."
          ctaText="Generate new"
          ctaIcon={IconPlus}
          onCtaClick={openFactFindFlow}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title="Fact Finds"
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      actions={
        <Button
          variant="primary"
          leftSection={<IconPlus size={16} />}
          size="md"
          onClick={openFactFindFlow}
        >
          Generate
        </Button>
      }
    >
      <Table
        variant="bare"
        captionSide="bottom"
        classNames={{ th: dataCaptureClasses.th }}
        verticalSpacing={'sm'}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>File name</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {isLoading ? (
            <Table.Tr>
              <Table.Td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                <Loader size="sm" />
              </Table.Td>
            </Table.Tr>
          ) : (
            rows
          )}
        </Table.Tbody>
      </Table>
    </FileUploadLayout>
  );
}
